'use client';
import React from 'react';
import Link from 'next/link';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';

const useCases = [
  {
    tag: '[01]',
    title: 'Building a security programme from the ground up',
    description:
      'Organisations that have grown fast and now need a coherent security programme, not a collection of tools and policies that do not connect. We design and build a programme that reflects how your organisation actually works.',
  },
  {
    tag: '[02]',
    title: 'Replacing a generic framework with something that fits',
    description:
      'Many organisations inherit a security programme built around a generic framework that does not map to their actual risk profile. We replace it with something specific, controls that make sense for your environment, your team, and your threat model.',
  },
  {
    tag: '[03]',
    title: 'Mapping an existing programme to compliance frameworks',
    description:
      'You have security controls in place but they are not mapped to SOC2, ISO 27001, DORA, or GDPR. We take what you have, identify the gaps, and build the mapping, so you get compliance credit for the security work you have already done.',
  },
  {
    tag: '[04]',
    title: 'Aligning controls across multiple frameworks simultaneously',
    description:
      'Organisations facing multiple compliance requirements, SOC2 and ISO 27001, or ISO 27001 and DORA, need a programme that satisfies all of them without duplicating effort. We design the control architecture to serve all frameworks from a single set of activities.',
  },
];

const outcomes = [
  { metric: 'Bespoke', label: 'Built for your organisation, not a template' },
  { metric: 'Multi-framework', label: 'One programme, many compliance mappings' },
  { metric: 'Owned', label: 'Your team can run it without us' },
  { metric: 'Durable', label: 'Designed to evolve with your organisation' },
];

const frameworks = [
  { name: 'SOC 2', type: 'Type I & II', desc: 'Trust Services Criteria mapping across Security, Availability, Confidentiality, Processing Integrity, and Privacy.' },
  { name: 'ISO 27001', type: '2022 Edition', desc: 'Annex A control mapping, ISMS design, Statement of Applicability, and risk treatment plan.' },
  { name: 'DORA', type: 'EU Regulation', desc: 'Digital Operational Resilience Act, ICT risk management, incident reporting, and third-party risk controls.' },
  { name: 'GDPR', type: 'Data Protection', desc: 'Technical and organisational measures, data processing controls, and privacy-by-design integration.' },
  { name: 'NIST CSF', type: 'v2.0', desc: 'Identify, Protect, Detect, Respond, Recover, mapped to your specific risk profile and operational context.' },
  { name: 'CIS Controls', type: 'v8', desc: 'Implementation Group mapping aligned to your organisation size, sector, and threat exposure.' },
];

const differentiators = [
  {
    title: 'No generic frameworks',
    body: 'We do not hand you a framework template and call it a programme. Every control, every policy, every process is designed for your organisation, your technology, your team, your risk profile.',
  },
  {
    title: 'Controls that connect to compliance',
    body: 'Every control we design is mapped to the frameworks you need to satisfy. You get a programme that is both operationally meaningful and compliance-ready, not two separate things.',
  },
  {
    title: 'Built for your team to own',
    body: 'We design programmes that your team can operate without us. Clear ownership, documented processes, and tooling that your engineers can maintain, not a dependency on external consultants.',
  },
];

export default function SecurityProgrammesPage() {
  useScrollAnimation();

  return (
    <div className="min-h-screen bg-[#050a0f] text-[#f5f5f8] noise-overlay-animated">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-28 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="orb orb-blue" style={{ width: '650px', height: '650px', top: '-180px', left: '-120px', opacity: 0.3 }} />
          <div className="orb orb-teal" style={{ width: '420px', height: '420px', bottom: '-30px', right: '8%', opacity: 0.2 }} />
        </div>
        <div className="relative z-10 max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="flex items-center gap-3 mb-6 fade-in-up">
            <Link href="/" className="font-mono-brand text-[0.65rem] text-[#555] uppercase tracking-widest hover:text-[#80edfa] transition-colors">Home</Link>
            <span className="text-[#333]">/</span>
            <Link href="/services" className="font-mono-brand text-[0.65rem] text-[#555] uppercase tracking-widest hover:text-[#80edfa] transition-colors">Services</Link>
            <span className="text-[#333]">/</span>
            <span className="font-mono-brand text-[0.65rem] text-[#80edfa] uppercase tracking-widest">Security Programmes</span>
          </div>

          <div className="max-w-[900px]">
            <p className="font-mono-brand text-[0.7rem] text-[#80edfa] uppercase tracking-widest mb-4 fade-in-up" style={{ transitionDelay: '0.05s' }}>
              [Bespoke Organisational Security Programmes]
            </p>
            <h1 className="font-heading text-[clamp(2.5rem,6vw,4.5rem)] text-[#f5f5f8] font-medium leading-[1.05] mb-6 fade-in-up" style={{ transitionDelay: '0.1s' }}>
              A security programme<br />
              <span className="text-[#80edfa]">built for your organisation.</span>
            </h1>
            <p className="text-[#aaa] text-lg leading-relaxed max-w-[680px] mb-10 fade-in-up" style={{ transitionDelay: '0.15s' }}>
              Generic frameworks do not reflect how your organisation works, what you are actually protecting, or where your real risks sit. We design bespoke security programmes, specific to your environment, your team, and your threat model, then map them to the compliance frameworks you need to satisfy. One programme. Multiple frameworks. Controls that actually make sense.
            </p>
            <div className="flex flex-wrap gap-4 fade-in-up" style={{ transitionDelay: '0.2s' }}>
              <a href="#contact" className="btn-primary">Discuss your programme</a>
              <a href="#frameworks" className="btn-secondary">Framework coverage</a>
            </div>
          </div>
        </div>
      </section>

      {/* Outcomes */}
      <section className="border-t border-[#222] py-16 bg-[#050a0f]">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-[#222]">
            {outcomes?.map((o, i) => (
              <div key={i} className="bg-[#050a0f] px-8 py-10 fade-in-up" style={{ transitionDelay: `${i * 0.08}s` }}>
                <p className="font-heading text-[clamp(1.5rem,3vw,2.25rem)] text-[#80edfa] font-medium mb-2">{o?.metric}</p>
                <p className="font-mono-brand text-[0.65rem] text-[#555] uppercase tracking-widest leading-relaxed">{o?.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="border-t border-[#222] py-20 lg:py-28">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="flex flex-col lg:flex-row gap-12 lg:gap-24 mb-16">
            <div className="lg:w-1/3 fade-in-left">
              <p className="font-mono-brand text-[0.7rem] text-[#80edfa] uppercase tracking-widest mb-4">Use cases</p>
              <h2 className="font-heading text-[clamp(1.75rem,3.5vw,2.75rem)] text-[#f5f5f8] font-medium leading-tight">
                When a bespoke programme is the right answer
              </h2>
            </div>
            <div className="lg:w-1/2 flex items-end fade-in-right" style={{ transitionDelay: '0.1s' }}>
              <p className="text-[#aaa] text-base leading-relaxed">
                Generic frameworks are a starting point, not a destination. These are the situations where a programme designed specifically for your organisation makes the real difference.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-px bg-[#222]">
            {useCases?.map((uc, i) => (
              <div key={i} className="bg-[#050a0f] p-8 lg:p-10 fade-in-up" style={{ transitionDelay: `${i * 0.08}s` }}>
                <p className="font-mono-brand text-[0.65rem] text-[#80edfa] uppercase tracking-widest mb-4">{uc?.tag}</p>
                <h3 className="font-heading text-xl text-[#f5f5f8] font-medium mb-3">{uc?.title}</h3>
                <p className="text-sm text-[#aaa] leading-relaxed">{uc?.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Framework coverage */}
      <section id="frameworks" className="border-t border-[#222] py-20 lg:py-28 bg-[#0a0f14]">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="mb-14 fade-in-up">
            <p className="font-mono-brand text-[0.7rem] text-[#80edfa] uppercase tracking-widest mb-4">Framework coverage</p>
            <h2 className="font-heading text-[clamp(1.75rem,3.5vw,2.75rem)] text-[#f5f5f8] font-medium leading-tight max-w-[600px]">
              One programme mapped to every framework you need
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#222]">
            {frameworks?.map((f, i) => (
              <div key={i} className="bg-[#0a0f14] p-8 fade-in-up" style={{ transitionDelay: `${i * 0.07}s` }}>
                <div className="flex items-start justify-between mb-4">
                  <p className="font-heading text-xl text-[#f5f5f8] font-medium">{f?.name}</p>
                  <span className="font-mono-brand text-[0.6rem] text-[#80edfa] uppercase tracking-widest border border-[#80edfa]/30 px-2 py-1">{f?.type}</span>
                </div>
                <p className="text-sm text-[#666] leading-relaxed">{f?.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Differentiators */}
      <section className="border-t border-[#222] py-20 lg:py-28">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="mb-14 fade-in-up">
            <p className="font-mono-brand text-[0.7rem] text-[#80edfa] uppercase tracking-widest mb-4">Why TechSonar</p>
            <h2 className="font-heading text-[clamp(1.75rem,3.5vw,2.75rem)] text-[#f5f5f8] font-medium leading-tight max-w-[600px]">
              Security programmes that reflect reality
            </h2>
          </div>

          <div className="flex flex-col lg:flex-row gap-px bg-[#222]">
            {differentiators?.map((d, i) => (
              <div key={i} className="flex-1 bg-[#050a0f] p-8 lg:p-10 fade-in-up" style={{ transitionDelay: `${i * 0.1}s` }}>
                <div className="w-8 h-px bg-[#80edfa] mb-6" />
                <h3 className="font-heading text-xl text-[#f5f5f8] font-medium mb-3">{d?.title}</h3>
                <p className="text-sm text-[#aaa] leading-relaxed">{d?.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-[#222] py-20 bg-[#0a0f14]">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
          <div className="fade-in-left">
            <p className="font-mono-brand text-[0.7rem] text-[#80edfa] uppercase tracking-widest mb-3">Get started</p>
            <h2 className="font-heading text-[clamp(1.5rem,3vw,2.25rem)] text-[#f5f5f8] font-medium">
              Ready to build a programme that actually fits?
            </h2>
            <p className="text-[#aaa] text-sm mt-2">Tell us about your organisation and the frameworks you need to satisfy, we will design the right programme.</p>
          </div>
          <div className="flex flex-wrap gap-4 fade-in-right" style={{ transitionDelay: '0.1s' }}>
            <a href="#contact" className="btn-primary">Book a scoping call</a>
            <Link href="/services" className="btn-secondary">View all services</Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
